/** Pathname ↔ view mapping for shareable public pages (SPA routes). */
export const PATHNAME_TO_VIEW = {
  '/privacy-policy': 'privacy-policy',
  '/about-us': 'about-us',
  '/fabric-templates': 'fabric-templates',
  '/browse-vendors': 'browse-vendors',
  '/promoters': 'promoters',
};

export const VIEW_TO_PATHNAME = Object.fromEntries(
  Object.entries(PATHNAME_TO_VIEW).map(([path, view]) => [view, path])
);

/** Nested gallery/history layers that must map back to a parent app view. */
export const NESTED_HISTORY_VIEW_TO_PARENT = {
  'collection-detail': 'fabricmarketplace',
  'fabric-detail': 'fabricmarketplace',
  'subscribed-collection-detail': 'mysubscriptions',
};

/** Resolve top-level app view from a history state object. */
export function resolveAppViewFromHistoryState(state) {
  const view = state?.view;
  if (!view) return null;
  return NESTED_HISTORY_VIEW_TO_PARENT[view] || view;
}

/** Preserve hash when pushing nested marketplace history entries. */
export function getMarketplaceHistoryUrl() {
  if (typeof window === 'undefined') return '/#fabricmarketplace';
  return `${window.location.pathname}${window.location.search || ''}${
    window.location.hash || '#fabricmarketplace'
  }`;
}

/** Named app routes that must not be treated as vendor phone numbers. */
const EXCLUDED_PATH_SLUGS = [
  'home',
  'login',
  'signup',
  'generate',
  'generate-lite',
  'myproducts',
  'myproducts-lite',
  'mykits',
  'inspirational',
  'moodboard',
  'virtualtry',
  'mycatalogue',
  'pricing',
  'adminpanel',
  'settings',
  'brandlibrary',
  'dashboard',
  'follow',
  'privacy-policy',
  'about-us',
  'fabric-templates',
  'browse-vendors',
  'promoters',
  'public-promoter',
  'public-catalogue',
  'ref',
  'auth',
  'moodboards'
];

export function isExcludedAppPath(pathname = '') {
  const path = pathname || '';
  return EXCLUDED_PATH_SLUGS.some(
    (slug) => path === `/${slug}` || path.startsWith(`/${slug}/`)
  );
}

export function parsePublicVendorPhonePath(pathname = '') {
  const path = pathname || '';
  if (isExcludedAppPath(path)) return null;
  const match = path.match(/^\/(\d{10,})$/);
  return match ? match[1] : null;
}

/** Public vendor page slug (username), e.g. /acme-fabrics */
export function parsePublicUsernamePath(pathname = '') {
  const path = pathname || '';
  if (isExcludedAppPath(path)) return null;
  const match = path.match(/^\/([a-z0-9][a-z0-9_-]{2,29})$/i);
  if (!match) return null;
  const slug = match[1].toLowerCase();
  if (/^\d{10,}$/.test(slug)) return null;
  return slug;
}

export function parsePublicVendorSlug(pathname = '') {
  return parsePublicVendorPhonePath(pathname) || parsePublicUsernamePath(pathname);
}

export function parseFollowPhonePath(pathname = '') {
  const match = (pathname || '').match(/^\/follow\/(\d{10,})$/);
  return match ? match[1] : null;
}

export function parsePublicPromoterPhonePath(pathname = '') {
  const match = (pathname || '').match(/^\/public-promoter\/(\d{10,})$/);
  return match ? match[1] : null;
}

export function parsePublicCatalogueId(pathname = '') {
  const match = (pathname || '').match(/^\/public-catalogue\/([a-zA-Z0-9]+)$/);
  return match ? match[1] : null;
}

export function parseInfluencerRefCode(pathname = '') {
  const match = (pathname || '').match(/^\/ref\/([A-Za-z0-9]{4,20})$/i);
  return match ? match[1].toUpperCase() : null;
}

const OAUTH_CALLBACK_STORAGE_KEY = 'fashdeck_oauth_callback';

export function isOAuthCallbackPath(pathname = '') {
  return pathname === '/auth/callback';
}

/** Keep the Google return payload before history sync rewrites the URL to the landing page. */
export function captureOAuthCallbackFromLocation() {
  if (typeof window === 'undefined') return;
  if (!isOAuthCallbackPath(window.location.pathname || '')) return;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const success = params.get('success') || '';
  const error = params.get('error') || '';
  if (!token && !error && success !== 'false') return;
  try {
    sessionStorage.setItem(
      OAUTH_CALLBACK_STORAGE_KEY,
      JSON.stringify({ token, success, error })
    );
  } catch (_) {}
}

export function hasPendingOAuthCallback() {
  if (typeof window !== 'undefined' && isOAuthCallbackPath(window.location.pathname || '')) {
    const params = new URLSearchParams(window.location.search);
    if (params.get('token') || params.get('error') || params.get('success') === 'false') {
      return true;
    }
  }
  try {
    const raw = sessionStorage.getItem(OAUTH_CALLBACK_STORAGE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    return Boolean(data && (data.token || data.error || data.success === 'false'));
  } catch (_) {
    return false;
  }
}

export function consumeOAuthCallback() {
  let token = '';
  let success = '';
  let error = '';
  if (typeof window !== 'undefined' && isOAuthCallbackPath(window.location.pathname || '')) {
    const params = new URLSearchParams(window.location.search);
    token = params.get('token') || '';
    success = params.get('success') || '';
    error = params.get('error') || '';
  }
  if (!token && !error) {
    try {
      const data = JSON.parse(sessionStorage.getItem(OAUTH_CALLBACK_STORAGE_KEY) || 'null');
      if (data) {
        token = data.token || '';
        success = data.success || '';
        error = data.error || '';
      }
    } catch (_) {}
  }
  try {
    sessionStorage.removeItem(OAUTH_CALLBACK_STORAGE_KEY);
  } catch (_) {}
  return { token, success, error };
}

export function pathnamePreservesView(pathname = '') {
  return (
    isOAuthCallbackPath(pathname) ||
    !!getViewFromPathname(pathname) ||
    !!parsePublicVendorPhonePath(pathname) ||
    !!parsePublicUsernamePath(pathname) ||
    !!parseFollowPhonePath(pathname) ||
    !!parsePublicPromoterPhonePath(pathname) ||
    !!parsePublicCatalogueId(pathname) ||
    !!parseInfluencerRefCode(pathname) ||
    pathname === '/moodboards' ||
    /^\/moodboards\//.test(pathname || '')
  );
}

/** Build the browser URL for a view + optional route params. */
export function buildUrlForView(activeView, params = {}) {
  if (VIEW_TO_PATHNAME[activeView]) {
    return VIEW_TO_PATHNAME[activeView];
  }
  if (activeView === 'public-fabric' && (params.publicVendorSlug || params.publicPhoneNumber)) {
    return `/${params.publicVendorSlug || params.publicPhoneNumber}`;
  }
  if (activeView === 'follow' && params.followPhoneNumber) {
    return `/follow/${params.followPhoneNumber}`;
  }
  if (activeView === 'public-promoter' && params.publicPromoterPhoneNumber) {
    return `/public-promoter/${params.publicPromoterPhoneNumber}`;
  }
  if (activeView === 'public-catalogue' && params.publicCatalogueId) {
    return `/public-catalogue/${params.publicCatalogueId}`;
  }
  if (activeView === 'influencer-ref' && params.influencerRefCode) {
    return `/ref/${params.influencerRefCode}`;
  }
  if (activeView === 'home') {
    return '/';
  }
  if (
    activeView === 'oauth-callback' &&
    typeof window !== 'undefined' &&
    isOAuthCallbackPath(window.location.pathname || '')
  ) {
    return `${window.location.pathname}${window.location.search || ''}`;
  }
  return `/#${activeView}`;
}

export function getViewFromPathname(pathname = '') {
  return PATHNAME_TO_VIEW[pathname] || null;
}

/** Resolve the first view from the current URL (pathname, then hash). */
export function resolveInitialActiveView() {
  if (typeof window === 'undefined') return 'home';

  captureOAuthCallbackFromLocation();

  const path = window.location.pathname || '';
  if (isOAuthCallbackPath(path) || hasPendingOAuthCallback()) return 'oauth-callback';
  const fromPath = getViewFromPathname(path);
  if (fromPath) return fromPath;

  if (parsePublicCatalogueId(path)) return 'public-catalogue';
  if (parseInfluencerRefCode(path)) return 'influencer-ref';
  if (parseFollowPhonePath(path)) return 'follow';
  if (parsePublicPromoterPhonePath(path)) return 'public-promoter';
  if (parsePublicVendorPhonePath(path)) return 'public-fabric';
  if (parsePublicUsernamePath(path)) return 'public-fabric';

  if (path === '/moodboards' || /^\/moodboards\//.test(path)) {
    return 'mykits';
  }

  const hash = (window.location.hash || '').replace(/^#/, '').split('?')[0];
  if (hash && NESTED_HISTORY_VIEW_TO_PARENT[hash]) {
    return NESTED_HISTORY_VIEW_TO_PARENT[hash];
  }
  if (hash) return hash;

  return 'home';
}

/** Parse public route params from the current pathname. */
export function parseRouteParamsFromPathname(pathname = '') {
  const path = pathname || '';
  const publicVendorSlug = parsePublicVendorSlug(path);
  return {
    publicVendorSlug,
    publicPhoneNumber: publicVendorSlug,
    followPhoneNumber: parseFollowPhonePath(path),
    publicPromoterPhoneNumber: parsePublicPromoterPhonePath(path),
    publicCatalogueId: parsePublicCatalogueId(path),
    influencerRefCode: parseInfluencerRefCode(path),
  };
}
